import type { SubTradeRequest } from '@ovComponents/5-page/portfolioTradingHub/types';
import {
  buildConflictKey,
  isConflictingTrade,
} from '@ovComponents/5-page/portfolioTradingHub/utils/reviewRebalanceCalculations';
import { useMemo } from 'react';
import type { StagedTrade } from './types';

export const computeConflictKeys = (
  stagedTrades: StagedTrade[],
  pendingSTRs: SubTradeRequest[],
  selectedSubAccountIds: Set<string>,
): Set<string> => {
  const conflictKeys = new Set<string>();
  const stagedKeys = new Set<string>();

  for (const trade of stagedTrades) {
    if (!trade.subAccountId || !trade.financialProductId) continue;
    stagedKeys.add(
      buildConflictKey(trade.subAccountId, trade.financialProductId),
    );
  }

  for (const str of pendingSTRs) {
    const subAccountId = str.subAccount?.id;
    const financialProductId = str.financialProduct?.id;
    if (!subAccountId || !financialProductId) continue;
    if (!selectedSubAccountIds.has(subAccountId)) continue;

    const key = buildConflictKey(subAccountId, financialProductId);
    if (!stagedKeys.has(key)) continue;
    conflictKeys.add(key);
  }

  return conflictKeys;
};

export const useConflictKeys = (
  stagedTrades: StagedTrade[],
  pendingSTRs: SubTradeRequest[],
  selectedSubAccountIds: Set<string>,
) =>
  useMemo(
    () => computeConflictKeys(stagedTrades, pendingSTRs, selectedSubAccountIds),
    [stagedTrades, pendingSTRs, selectedSubAccountIds],
  );

export const filterActiveTrades = (
  trades: StagedTrade[],
  conflictKeys: Set<string>,
): StagedTrade[] =>
  trades.filter(
    (trade) =>
      !trade.removed &&
      Boolean(trade.financialProductId) &&
      !isConflictingTrade(trade, conflictKeys),
  );
